import { useSuspenseQueries } from '@tanstack/react-query';
import { memo } from 'react';

import type * as api from '@gitbook/api-client';
import type { NavigationScreenState } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import { SidebarBody, SidebarHeader, SidebarItemList, SidebarLink } from '@gitbook/spine-react';

import { NotificationsBell, OrganizationSearchButton } from '../../Components';
import { apiQuery } from '../../state';
import {
    AgentsLink,
    ChangeRequestsLink,
    ContentSidebarFooter,
    HomeLink,
    SitesLink,
} from './ContentSidebar';
import { OrganizationButton } from './OrganizationButton';
import { SitesDisclosure } from './Sites';
import { SpacesDisclosure } from './Spaces';
import { useAutoExpandCollections } from './useAutoExpandCollections';

/**
 * Legacy content sidebar displayed when site-centric navigation is disabled.
 */
export function ContentSidebarContentWithOrgLegacy(props: {
    organizationId: string;
    screen: NavigationScreenState;
}) {
    const { organizationId, screen } = props;

    const [{ data: organization }, { data: orgAllContent }] = useSuspenseQueries({
        queries: [
            apiQuery('getOrganizationById', { organizationId }),
            apiQuery('getOrganizationAllContent', { organizationId }),
        ],
    });

    useAutoExpandCollections({ orgAllContent, screen });

    return (
        <>
            <ContentSidebarHeader organization={organization} screen={screen} />

            <SidebarBody aria-label="Main navigation" className="pb-10">
                <SidebarItemList>
                    <HomeLink screen={screen} />
                    <SitesLink screen={screen} />
                    <ChangeRequestsLink screen={screen} />
                    <AgentsLink screen={screen} />
                    <OpenAPILink screen={screen} />
                    <TranslationsLink screen={screen} />
                    <IntegrationsLink screen={screen} />
                </SidebarItemList>
                <SitesDisclosure
                    screen={screen}
                    content={orgAllContent}
                    organization={organization}
                />
                <SpacesDisclosure
                    content={orgAllContent}
                    organization={organization}
                    screen={screen}
                />
            </SidebarBody>

            <ContentSidebarFooter organization={organization} screen={screen} />
        </>
    );
}

const ContentSidebarHeader = memo(function ContentSidebarHeader(props: {
    organization: api.Organization;
    screen: NavigationScreenState;
}) {
    const { organization, screen } = props;
    return (
        <SidebarHeader>
            <div className="flex flex-1">
                <OrganizationButton organization={organization} screen={screen} />
            </div>
            <div className="flex items-center gap-0.5">
                <OrganizationSearchButton
                    data-testid="sidebar.findButton"
                    organization={organization}
                />
                <NotificationsBell iconOnly />
            </div>
        </SidebarHeader>
    );
});

function OpenAPILink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="OpenAPI"
            icon={Icon.OpenAPI}
            isActive={screen.type === 'openapi_spec' || screen.type === 'openapi_specs'}
            href={{
                screen: {
                    type: 'openapi_specs',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}

function TranslationsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Translations"
            icon={Icon.AITranslate}
            isActive={screen.type === 'translations'}
            href={{
                screen: {
                    type: 'translations',
                    organization: 'organization' in screen ? screen.organization : undefined,
                    view: 'overview',
                },
            }}
        />
    );
}

function IntegrationsLink(props: { screen: NavigationScreenState }) {
    const { screen } = props;
    return (
        <SidebarLink
            label="Integrations"
            icon={Icon.Integrations}
            isActive={screen.type === 'integrations'}
            href={{
                screen: {
                    type: 'integrations',
                    organization: 'organization' in screen ? screen.organization : undefined,
                },
            }}
        />
    );
}
